import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'

import { SealType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import { blankSealType } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormFieldArray from 'components/p5/FormFieldArray'
import FormField from 'components/p5/FormField'
import Input from 'components/p5/Input'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

function Seals({ transportEquipmentIndex } : { transportEquipmentIndex: number }) {
  const { t } = useTranslation()

  const { control } = useFormContext<TransitOperationFormValues>()
  const scope = `transportEquipment.${transportEquipmentIndex}.seals` as const

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, SealType>({
    control,
    name: scope,
  })

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.identifier`),
  ], [scope, visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-transport-equipment-seals"
        headingLabel={t('declaration.p5.seals')}
        fieldRule={fieldRequirements[scope]}
        collapsible={false}
      >
        {
          visibleFields.map((fieldWithIndex) => {
            const sealItem = fieldWithIndex.field
            const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

            return (
              <FormFieldArray
                key={`sealItem_${sealItem.id}`}
                titleKey="declaration.p5.sequenceNumber"
                removeItem={() => softRemove(fieldWithIndex.index)}
                hidden={sealItem.deleted}
                sequenceNumber={sealItem.sequenceNumber + 1}
              >
                <FormField
                  labelKey="declaration.p5.sealIdentifier"
                  fieldRule={fieldRequirements[`${innerItemScope}.identifier`]}
                >
                  <Input
                    field={`${innerItemScope}.identifier`}
                    labelKey="declaration.p5.sealIdentifier"
                    type="text"
                    placeholder={t('common.fieldPlaceholder')}
                    fieldRule={fieldRequirements[`${innerItemScope}.identifier`]}
                  />
                </FormField>
              </FormFieldArray>
            )
          })
        }
      </FormCollapsibleColumn>
      <div className="row">
        <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
          <button
            type="button"
            className="btn btn-sm btn-primary mt-2"
            onClick={() => appendWithSequence(blankSealType)}
            disabled={isAddForbidden}
          >
            <i className="fa fa-plus me-2" />
            {t('declaration.p5.addNewSeal')}
          </button>
        </div>
      </div>

    </section>
  )
}

export default Seals
